'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useCharacterData, useGlobalChatState, useChatStatus } from '../../hooks/useGlobalChatState';
import { ChatStatus } from '../../data/globalChatState';
import LoginPopup from '@/app/popup/login-popup';
import { Loader2 } from 'lucide-react';
import { useTagNames } from '@/app/lib/avatar-tags/useTags';
import { SingleTag } from '@/app/lib/avatar-tags/tagTypes';
import { ResetChatConfirmPopup } from './Popups';
import ChatSettingPopup from './ChatSettingPopup';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import UserBalancePopup, { getCurrentUrlForCallback } from '@/app/subscription/user-credit/frontend/userBalancePopup';
import { languageModels } from '../../data/chatSetting/settingOptions';
import { useTranslations } from 'next-intl';

interface SidePanelContentProps {
  onClose: () => void;
  showProfileImage?: boolean;
  showFirstGreeting?: boolean;
  className?: string;
}

export function SidePanelContent({ 
  onClose,
  className = ""
}: SidePanelContentProps) {
  const t = useTranslations('chat');
  const tTags = useTranslations('tags');
  const router = useRouter();
  const { session, userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginRedirectUrl, setLoginRedirectUrl] = useState('/');
  const [isOwner, setIsOwner] = useState(false);
  const [ownershipChecked, setOwnershipChecked] = useState(false);
  const [showResetConfirmPopup, setShowResetConfirmPopup] = useState(false);
  const [showChatSettingPopup, setShowChatSettingPopup] = useState(false);
  const [showBalancePopup, setShowBalancePopup] = useState(false);
  const [characterTags, setCharacterTags] = useState<SingleTag[]>([]);

  const { character } = useCharacterData();
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const { chatStatus } = useChatStatus();
  
  // User app data for balance
  const [userAppDataState] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );

  // Load character tags using the hook
  const tagNames = useTagNames(character?.metadata.tags || []);
  
  useEffect(() => {
    if (character?.metadata.tags && character.metadata.tags.length > 0) {
      const tags = character.metadata.tags.map((tagId, index) => {
        const tagName = tagNames[index];
        return { tag_id: tagId, tag_name: tagName || `Tag ${tagId}` };
      }).filter(tag => tag.tag_name !== `Tag ${tag.tag_id}`); // Filter out unresolved tags
      setCharacterTags(tags);
    } else {
      setCharacterTags([]);
    }
  }, [character?.metadata.tags, tagNames]);

  // Check ownership when component mounts and user is logged in
  useEffect(() => {
    const checkOwnership = async () => {
      if (userEmail && character?.metadata.avatar_id) {
        try {
          const { isUserAvatarOwnerAction } = await import('@/app/lib/actions');
          const ownershipResponse = await isUserAvatarOwnerAction(userEmail, character.metadata.avatar_id);
          setIsOwner(ownershipResponse.isOwner);
        } catch (error) {
          console.error('Error checking ownership:', error);
          setIsOwner(false);
        }
      } else {
        setIsOwner(false);
      }
      setOwnershipChecked(true);
    };
    
    checkOwnership();
  }, [userEmail, character?.metadata.avatar_id]);

  const handleEdit = () => {
    if (!session) {
      setLoginRedirectUrl(`/edit-character/${character?.metadata.avatar_id}`);
      setShowLoginPopup(true);
      return;
    }
    // Navigate to edit page
    router.push(`/edit-character/${character?.metadata.avatar_id}`);
  };

  const handleResetChat = () => {
    if (!session || !character?.metadata.avatar_id) {
      return;
    }
    setShowResetConfirmPopup(true);
  };

  // Don't render anything if no character
  if (!character) return null;

  return (
    <>
      <div className={`flex flex-col gap-4 pb-6 ${className}`}>
        {/* Character Info */}
        <div className="flex flex-col gap-4">
          {/* Avatar Name */}
          <div className="flex flex-col gap-1">
            <h4 className="text-white text-lg font-semibold font-['Montserrat',Helvetica]">
              {character.metadata.avatar_name || 'Unknown Avatar'}
            </h4>
            <p className="text-[#8f9092] text-sm flex items-center gap-2 font-['Montserrat',Helvetica]">
              {ownershipChecked && isOwner && (
                <>
                  <span>•</span>
                  <span>{t('ownedByYou')}</span>
                </>
              )}
            </p>
          </div>
          
          {/* Bio Section */}
          {character.metadata.agent_bio && (
            <div className="flex flex-col gap-2">
              <h5 className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">{t('bio')}</h5>
              <p className="text-white/70 text-sm leading-relaxed font-['Montserrat',Helvetica]">
                {character.metadata.agent_bio}
              </p>
            </div>
          )}

          {/* Tags Section */}
          {characterTags.length > 0 && (
            <div className="flex flex-col gap-2">
              <h5 className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">{t('tags')}</h5>
              <div className="flex flex-wrap gap-2">
                {characterTags.map((tag) => (
                  <div
                    key={tag.tag_id}
                    className="px-3 py-1.5 bg-[#2a2a2e] text-white text-sm rounded-full font-['Montserrat',Helvetica]"
                  >
                    {tTags(tag.tag_name)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Edit Button - moved to end of character info */}
        {ownershipChecked && isOwner && (
          <button
            onClick={handleEdit}
            className="w-full px-4 py-3 bg-[#2a2a2e] hover:bg-[#ffffff1a] rounded-xl text-sm text-white transition-all duration-200 font-['Montserrat',Helvetica] font-medium border border-white/5 hover:border-white/10"
          >
            {t('editCharacter')}
          </button>
        )}

        {/* Chat Settings Section */}
        <div className="flex flex-col gap-2">
          <h4 className="text-white text-lg font-semibold font-['Montserrat',Helvetica]">{t('chatSettings')}</h4>
          
          {/* Balance and Current Model Row - Side by Side */}
          <div className="flex gap-2">
            {/* Balance */}
            {isAuthenticated && (
              <div className="flex items-center gap-2 bg-[#2a2a2e] rounded-xl p-3 border border-white/5 hover:border-white/10 transition-all flex-1">
                <Image
                  src="/credit_icons/gem.png"
                  alt="Gem"
                  width={16}
                  height={16}
                  className="w-4 h-4"
                />
                <span className="text-white text-sm font-medium font-['Montserrat',Helvetica]">
                  {authLoading || !userAppDataState.isInitialized ? (
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                  ) : (
                    userAppDataState.creditCount + userAppDataState.dailyCreditCount
                  )}
                </span>
                <button
                  onClick={() => setShowBalancePopup(true)}
                  className="text-[#8f9092] hover:text-white transition-colors ml-auto"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            )}
            
            {/* Current LLM */}
            <button
              onClick={(chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE) ? undefined : () => setShowChatSettingPopup(true)}
              disabled={chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE}
              className={`flex items-center justify-between p-3 rounded-xl transition-all duration-200 border font-['Montserrat',Helvetica] flex-1 ${
                chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE 
                  ? 'bg-[#2a2a2e] border-white/5 cursor-not-allowed opacity-60' 
                  : 'bg-[#2a2a2e] hover:bg-[#ffffff1a] border-white/5 hover:border-white/10'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-sm">{t('model')}</span>
                <span className="text-white text-sm font-medium">
                  {(chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE)
                    ? t('gemini')
                    : (() => {
                        const model = languageModels[globalChatStateData.userChatSetting.languageModel];
                        return model ? model.name : t('unknown');
                      })()
                  }
                </span>
              </div>
              {(chatStatus !== ChatStatus.Call && chatStatus !== ChatStatus.LIVE) && (
                <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Action Buttons Section */}
        <div className="flex flex-col gap-2 mt-2">
          {/* Start New Chat Button */}
          {session && (
            <button
              onClick={handleResetChat}
              className="w-full px-4 py-3 bg-[#2a2a2e] hover:bg-[#ffffff1a] rounded-xl text-sm text-white transition-all duration-200 font-['Montserrat',Helvetica] font-medium border border-white/5 hover:border-white/10"
            >
              {t('startANewChat')}
            </button>
          )}
        </div>
      </div>

      {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => {
          setShowLoginPopup(false);
        }}
        redirectTarget={loginRedirectUrl}
      />

      {/* Reset Chat Confirmation Popup */}
      <ResetChatConfirmPopup
        isOpen={showResetConfirmPopup}
        onClose={() => setShowResetConfirmPopup(false)}
        avatarId={character?.metadata.avatar_id || ''}
        onSuccess={onClose}
      />

      {/* Chat Setting Popup */}
      <ChatSettingPopup
        isOpen={showChatSettingPopup}
        onClose={() => setShowChatSettingPopup(false)}
      />

      {/* User Balance Popup */}
      <UserBalancePopup
        isOpen={showBalancePopup}
        onClose={() => setShowBalancePopup(false)}
        callbackBaseUrl={getCurrentUrlForCallback()}
      />
    </>
  );
}